import type { Language } from "../strings";
import type { CorpusIndexShort } from "./corpus-types";

/** Keep the library compact; the standalone Flechas index receives every entry. */
export function latestFlechas(entries: readonly CorpusIndexShort[]): CorpusIndexShort[] {
  const addedTime = (entry: CorpusIndexShort): number => {
    const timestamp = Date.parse(entry.added_at ?? "");
    return Number.isFinite(timestamp) ? timestamp : 0;
  };
  return [...entries]
    .sort((a, b) => addedTime(b) - addedTime(a) || compareFlechaPresentation(a, b))
    .slice(0, 5);
}

/**
 * Reader JSON is a build-time snapshot and cannot prove the live RAG marker is
 * still ready. Keep the availability notice visible even when its historical
 * `promoted`/`served` flags were true at artifact-generation time.
 */
export function showFlechaChatAvailabilityNotice(
  _status: { promoted: boolean; served: boolean },
): true {
  return true;
}

/** Mirror the artifact contract so cached manifests cannot fall back to YouTube-id order. */
export function compareFlechaPresentation(a: CorpusIndexShort, b: CorpusIndexShort): number {
  const ad = a.presentation_order?.published_at ?? null;
  const bd = b.presentation_order?.published_at ?? null;
  if (ad !== null && bd !== null && ad !== bd) return Date.parse(bd) - Date.parse(ad);
  if (ad !== null && bd === null) return -1;
  if (ad === null && bd !== null) return 1;
  return (
    (a.presentation_order?.topic_index ?? Number.MAX_SAFE_INTEGER) -
      (b.presentation_order?.topic_index ?? Number.MAX_SAFE_INTEGER) ||
    (a.presentation_order?.short_index ?? Number.MAX_SAFE_INTEGER) -
      (b.presentation_order?.short_index ?? Number.MAX_SAFE_INTEGER) ||
    (b.presentation_order?.year ?? Number.MIN_SAFE_INTEGER) -
      (a.presentation_order?.year ?? Number.MIN_SAFE_INTEGER) ||
    a.entry_id.localeCompare(b.entry_id)
  );
}

/** Preserve the source title elsewhere; remove only the hashtag tail for UI display. */
export function flechaDisplayTitle(
  title: string | null | undefined,
  fallback = "Aclaración de Koresh",
): string {
  if (typeof title !== "string") return fallback;
  const withoutTags = title.replace(/\s*#[^\s#]+(?:\s*#[^\s#]+)*\s*$/u, "").trim();
  return withoutTags || title.trim() || fallback;
}

export function flechaYear(slugs: string[]): number | null {
  for (const slug of slugs) {
    const match = /-(\d{4})$/.exec(slug);
    if (match) return Number(match[1]);
  }
  return null;
}

export function flechaAskPrompt(
  language: Language,
  title: string | null,
  section: string | null,
): string {
  const display = flechaDisplayTitle(
    title,
    language === "es" ? "Aclaración de Koresh" : "Koresh short teaching",
  );
  if (language === "en") {
    return section
      ? `Explain this section of Koresh's short teaching “${display}”: “${section}”.`
      : `Explain Koresh's short teaching “${display}”.`;
  }
  return section
    ? `Explícame esta sección de la enseñanza corta de Koresh «${display}»: «${section}».`
    : `Explícame la enseñanza corta de Koresh «${display}».`;
}
